import { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
  getFirstCollision,
  type CollisionDetection,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import Fuse from "fuse.js";
import { useStore } from "../../stores/store";
import { LaneColumn } from "./LaneColumn";
import { CardView } from "./CardView";
import type { Card } from "../../types";
import { api } from "../../lib/api";
import { matchDeadlinePreset, sortCards } from "../../lib/utils";
import { promptAsync } from "../../stores/dialogStore";

interface Props {
  onCardClick: (id: number) => void;
}

export function Board({ onCardClick }: Props) {
  const lanes = useStore((s) => s.lanes);
  const cards = useStore((s) => s.cards);
  const searchQuery = useStore((s) => s.searchQuery);
  const priorityFilter = useStore((s) => s.priorityFilter);
  const categoryFilter = useStore((s) => s.categoryFilter);
  const moodFilter = useStore((s) => s.moodFilter);
  const deadlineFilter = useStore((s) => s.deadlineFilter);
  const sort = useStore((s) => s.sort);
  const focusLaneId = useStore((s) => s.focusLaneId);
  const moveCardLocal = useStore((s) => s.moveCardLocal);
  const upsertCard = useStore((s) => s.upsertCard);

  const [activeId, setActiveId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const collisionDetection: CollisionDetection = (args) => {
    const pointer = pointerWithin(args);
    if (pointer.length > 0) {
      const laneFirst = pointer.find((c) => String(c.id).startsWith("lane-"));
      const cardHit = pointer.find((c) => !String(c.id).startsWith("lane-"));
      return cardHit ? [cardHit] : laneFirst ? [laneFirst] : pointer;
    }
    const rects = rectIntersection(args);
    const first = getFirstCollision(rects);
    return first ? [{ id: first as any }] : [];
  };

  const fuse = useMemo(
    () =>
      new Fuse(cards, {
        keys: ["title", "description", "notes", "whyItMatters"],
        threshold: 0.4,
        ignoreLocation: true,
      }),
    [cards],
  );

  const filtered = useMemo(() => {
    let base = cards;
    if (searchQuery.trim()) {
      const hits = fuse.search(searchQuery.trim());
      base = hits.map((h) => h.item);
    }
    return base.filter((c) => {
      if (priorityFilter && c.priority !== priorityFilter) return false;
      if (categoryFilter != null && !(c.categoryIds || []).includes(categoryFilter)) return false;
      if (moodFilter && c.mood !== moodFilter) return false;
      if (deadlineFilter && !matchDeadlinePreset(c.dueDate, deadlineFilter)) return false;
      return true;
    });
  }, [cards, fuse, searchQuery, priorityFilter, categoryFilter, moodFilter, deadlineFilter]);

  const byLane = useMemo(() => {
    const laneIds = new Set(lanes.map((l) => l.id));
    const seen = new Set<number>();
    const m = new Map<number, Card[]>();
    for (const l of lanes) m.set(l.id, []);
    for (const c of filtered) {
      if (typeof c.id !== "number" || !Number.isFinite(c.id)) continue;
      if (seen.has(c.id)) continue;
      if (!laneIds.has(c.laneId)) continue;
      seen.add(c.id);
      m.get(c.laneId)!.push(c);
    }
    for (const [k, arr] of m.entries()) {
      m.set(k, sortCards(arr, sort));
    }
    return m;
  }, [filtered, lanes, sort]);

  const visibleLanes = focusLaneId !== null ? lanes.filter((l) => l.id === focusLaneId) : lanes;

  const activeCard = activeId ? cards.find((c) => c.id === activeId) : null;

  function onDragStart(e: DragStartEvent) {
    const id = Number(e.active.id);
    setActiveId(id);
  }

  async function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    setActiveId(null);
    if (!over) return;

    const activeIdNum = Number(active.id);
    const card = cards.find((c) => c.id === activeIdNum);
    if (!card) return;

    const overData = over.data.current;
    let targetLaneId: number | undefined;
    let targetIndex: number | undefined;
    if (overData?.type === "lane") {
      targetLaneId = overData.laneId;
      const list = byLane.get(targetLaneId!) || [];
      targetIndex = list.length;
    } else if (overData?.type === "card") {
      targetLaneId = overData.laneId;
      const list = byLane.get(targetLaneId!) || [];
      const overIdx = list.findIndex((c) => c.id === Number(over.id));
      targetIndex = overIdx === -1 ? list.length : overIdx;
    }
    if (targetLaneId === undefined || targetIndex === undefined) return;

    if (card.laneId === targetLaneId) {
      const list = byLane.get(targetLaneId) || [];
      const currentIdx = list.findIndex((c) => c.id === activeIdNum);
      if (currentIdx === targetIndex) return;
    }

    moveCardLocal(activeIdNum, targetLaneId, targetIndex);
    try {
      const updated = await api.moveCard(activeIdNum, targetLaneId, targetIndex);
      upsertCard(updated);
    } catch (err) {
      console.error(err);
      await useStore.getState().load();
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div data-tour="board" className="grid-bg flex-1 overflow-x-auto overflow-y-hidden snap-x snap-mandatory md:snap-none scrollbar-thin">
        <div className="flex h-full flex-row items-stretch gap-3 md:gap-4 px-3 md:px-5 py-3 md:py-5">
          {visibleLanes.map((lane) => (
            <LaneColumn
              key={lane.id}
              lane={lane}
              cards={byLane.get(lane.id) || []}
              focused={focusLaneId !== null}
              onCardClick={onCardClick}
              onQuickAdd={async (laneId) => {
                const title = await promptAsync({
                  title: "Novo card",
                  placeholder: "Título do card",
                  confirmLabel: "Criar",
                });
                if (!title?.trim()) return;
                const card = await api.createCard({ title: title.trim(), laneId });
                upsertCard(card);
              }}
            />
          ))}
        </div>
      </div>
      <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" }}>
        {activeCard && (
          <div className="w-[min(300px,calc(100vw-40px))]">
            <CardView card={activeCard} onClick={() => {}} overlay />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
